/**
 * PBKDF2 password hashing (Web Crypto) for offline auth.
 * Format: pbkdf2$<iterations>$<saltB64>$<hashB64>
 */

const PBKDF2_PREFIX = 'pbkdf2';
const DEFAULT_ITERATIONS = 210_000;
const SALT_BYTES = 16;
const DERIVED_BITS = 256;

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function timingSafeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

function getGlobalRoot() {
  if (typeof window !== 'undefined') return window;
  if (typeof self !== 'undefined') return self;
  if (typeof global !== 'undefined') return global;
  return null;
}

function encodeUtf8(value) {
  const root = getGlobalRoot();
  const Encoder = root?.TextEncoder;
  if (!Encoder) {
    throw new Error('TextEncoder not available for password hashing');
  }
  return new Encoder().encode(String(value ?? ''));
}

function getWebCrypto() {
  // Browser / Electron renderer: window.crypto. Tests: bridged in setupTests.js.
  const root = getGlobalRoot();
  if (root?.crypto?.subtle) {
    return root.crypto;
  }
  return null;
}

export function isPbkdf2PasswordHash(stored) {
  return typeof stored === 'string' && stored.startsWith(`${PBKDF2_PREFIX}$`);
}

export async function hashPasswordPbkdf2(password) {
  const cryptoApi = getWebCrypto();
  if (!cryptoApi?.subtle?.importKey) {
    throw new Error('Web Crypto not available for password hashing');
  }

  const salt = cryptoApi.getRandomValues(new Uint8Array(SALT_BYTES));
  const keyMaterial = await cryptoApi.subtle.importKey(
    'raw',
    encodeUtf8(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const derived = await cryptoApi.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: DEFAULT_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    DERIVED_BITS
  );

  const hashBytes = new Uint8Array(derived);
  return `${PBKDF2_PREFIX}$${DEFAULT_ITERATIONS}$${bytesToBase64(salt)}$${bytesToBase64(hashBytes)}`;
}

export async function verifyPasswordPbkdf2(password, stored) {
  if (!isPbkdf2PasswordHash(stored)) return false;
  const cryptoApi = getWebCrypto();
  if (!cryptoApi?.subtle?.importKey) return false;

  const parts = stored.split('$');
  if (parts.length !== 4 || parts[0] !== PBKDF2_PREFIX) return false;

  const iterations = Number(parts[1]);
  if (!Number.isFinite(iterations) || iterations < 1) return false;

  const salt = base64ToBytes(parts[2]);
  const expected = base64ToBytes(parts[3]);

  const keyMaterial = await cryptoApi.subtle.importKey(
    'raw',
    encodeUtf8(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const derived = await cryptoApi.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256'
    },
    keyMaterial,
    expected.length * 8
  );

  return timingSafeEqual(new Uint8Array(derived), expected);
}
